import { motion, AnimatePresence } from 'framer-motion'
import type { StoredLike } from './useSwiperState'
import { ct, cmoney } from '@/lib/i18n/configurator'

interface LikesDrawerProps {
  open: boolean
  onClose: () => void
  likes: StoredLike[]
  onAddToWishlist: (like: StoredLike) => void
  onRemove: (key: string) => void
}

export function LikesDrawer({ open, onClose, likes, onAddToWishlist, onRemove }: LikesDrawerProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/30 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.div
            className="fixed top-0 right-0 bottom-0 w-[340px] max-w-[85vw] bg-white z-50 shadow-2xl flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {ct('swMyLikes', { n: likes.length })}
              </h2>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {likes.length === 0 ? (
                <div className="text-center text-gray-400 py-12">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <p className="text-sm">{ct('swEmpty')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {likes.map(like => (
                    <LikeCard
                      key={like.key}
                      like={like}
                      baseImage={like.baseImage}
                      onAddToWishlist={() => onAddToWishlist(like)}
                      onRemove={() => onRemove(like.key)}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function LikeCard({
  like,
  baseImage,
  onAddToWishlist,
  onRemove,
}: {
  like: StoredLike
  baseImage: string
  onAddToWishlist: () => void
  onRemove: () => void
}) {
  // Format historique en français (139,00 €), format du pays sinon
  const formatPrice = (cents: number) => cmoney(cents)

  return (
    <div className="relative group bg-gray-50 rounded-lg overflow-hidden">
      {/* Mini preview */}
      <div className="relative aspect-square bg-white">
        {baseImage && (
          <img
            src={baseImage}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            style={{ zIndex: 0 }}
          />
        )}
        {like.products.map(p => p.productImage && (
          <img
            key={p.productId}
            src={p.productImage}
            alt={p.productName}
            className="absolute inset-0 w-full h-full object-contain"
            style={{ zIndex: p.productLayer, pointerEvents: 'none' }}
          />
        ))}
      </div>

      {/* Price + actions */}
      <div className="p-1.5">
        <p className="text-xs text-center font-medium text-gray-700 mb-1">
          {formatPrice(like.totalPrice)}
        </p>
        <div className="flex gap-1">
          <button
            onClick={onAddToWishlist}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-[#171717] hover:bg-black text-white text-[10px] font-medium rounded transition-colors"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            {ct('wishlist')}
          </button>
          <button
            onClick={onRemove}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            title={ct('remove')}
            aria-label={ct('remove')}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
